import { useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type {
  CancellationToken,
  PivotDataResponse,
  ReportField,
  RunPivotReportRequest,
  SortField,
  ValueCell,
} from "../../../../../../api/types/svTypes.ts";
import useGridStateViewer from "../../../../reporting/sv/components/viewer/pivot/hooks/useGridStateViewer.ts";
import { useRequestConfigViewer } from "../../../../reporting/sv/components/viewer/pivot/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../../../reporting/sv/components/viewer/pivot/PreviewComponent.tsx";
import type { PivotRunState } from "../../../../reporting/sv/components/viewer/storage/runState.ts";
import { generateRandomString } from "../../../utils/generateRandomString.ts";
import type { DrillDownArgs } from "../DrillDownArgs.ts";
import SmartViewWidgetViewerWrapper from "../SmartViewWidgetViewerWrapper";

interface Props {
  runState: PivotRunState;
  runReport: (
    dataSetId: string,
    body: RunPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  onDrillDown: (args: DrillDownArgs) => void;
  onSortingUpdate?: (newSorting: SortField[]) => void;
}
const SmartViewPivotWidgetViewer = ({ runState, runReport, onDrillDown, onSortingUpdate }: Props) => {
  const [etag, setEtag] = useState<string | undefined>(undefined);
  const [sessionId] = useState<string>(generateRandomString);

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    values: runState.values,
    sorts: runState.sort,
    isQueryLoggingActive: false,
    sessionId: sessionId,
  });

  const gridState = useGridStateViewer({
    rows: runState.rows,
    columns: runState.columns,
    values: runState.values,
    sorts: runState.sort,
    requestConfig: viewConfig,
    updateDevTools: undefined,
    setEtag: setEtag,
    runReport,
  });

  const handleDrillDown = (field: ReportField, cell: ValueCell) => {
    onDrillDown({ field, cell, etag, sessionId });
  };

  return (
    <SmartViewWidgetViewerWrapper initialized={gridState.initialized} validity={gridState.configurationValidity}>
      <PreviewComponent
        gridState={gridState}
        columns={runState.columns}
        rows={runState.rows}
        values={runState.values}
        previewMode={"maximize"}
        settings={runState.settings}
        disableCellClick={true}
        onDrillDown={handleDrillDown}
        onSortingUpdate={onSortingUpdate}
        sorts={runState.sort}
      />
    </SmartViewWidgetViewerWrapper>
  );
};

export default SmartViewPivotWidgetViewer;
